import React, { useRef, useEffect } from "react";
import styles from "./task-list-table.module.css";
import { columnsType, Task } from "../../types/public-types";
import dropDown from "../../../../svg/dropDown.svg";

export const TaskListTableDefault: React.FC<{
  rowHeight: number;
  rowWidth: string;
  fontFamily: string;
  fontSize: string;
  locale: string;
  tasks: Task[];
  selectedTask: Task | undefined;
  setSelectedTask: (taskId: string) => void;
  onExpanderClick: (task: Task) => void;
  taskSelect: (task: Task) => void;
  taskDblClick: ((task: Task) => void) | undefined;
  selectedTaskId: string;
  columns: columnsType[];
  scrollY?: number;
  ganttHeight?: number;
  suppressTaskListExpander?: boolean;
  bulkMultiSelectActive?: boolean;
}> = ({
  rowHeight,
  rowWidth,
  tasks,
  fontFamily,
  fontSize,
  onExpanderClick,
  columns,
  taskSelect,
  taskDblClick,
  selectedTask,
  scrollY,
  ganttHeight,
  suppressTaskListExpander,
  bulkMultiSelectActive = false,
}) => {
    const taskref = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      if (selectedTask && taskref.current) {
        const selectedElement = taskref.current.querySelector(`[data-task-id="${selectedTask.id}"]`);
        if (selectedElement) {
          selectedElement.scrollIntoView({
            behavior: "instant",
            block: "center",
            inline: "nearest",
          });
        }
      }
    }, [selectedTask]);

    const buffer = 5;
    const startIndex = scrollY !== undefined
      ? Math.max(0, Math.floor(scrollY / rowHeight) - buffer)
      : 0;
    const endIndex = scrollY !== undefined && ganttHeight !== undefined && ganttHeight > 0
      ? Math.min(tasks.length, Math.ceil((scrollY + ganttHeight) / rowHeight) + buffer)
      : tasks.length;

    const visibleTasks = tasks.slice(startIndex, endIndex);

    const rowBulkPeekClass = `${styles.bulkRow}${
      bulkMultiSelectActive ? ` ${styles.bulkActive}` : ""
    }`;

    return (
      <div
        className={styles.taskListWrapper + " w-full select-none "}
        style={{
          fontFamily: fontFamily,
          fontSize: fontSize,
          paddingTop: `${startIndex * rowHeight}px`,
          paddingBottom: `${Math.max(0, (tasks.length - endIndex) * rowHeight)}px`
        }}
        ref={taskref}
      >
        {visibleTasks.map((t: any, rowIndex) => {
          let expanderSymbol: any;
          if (!suppressTaskListExpander) {
            if (t.hideChildren === false) {
              expanderSymbol = (
                <img
                  src={dropDown}
                  className="w-4 min-w-4 rotate-180 font-semibold"
                />
              );
            } else if (t.hideChildren === true) {
              expanderSymbol = (
                <img src={dropDown} className="w-4 min-w-4 rotate-90" />
              );
            }
          }
          return (
            <div
              key={`${t.id ?? "row"}-${startIndex + rowIndex}`}
              onClick={() => {
                setTimeout(() => {
                  taskSelect(t);
                }, 600);
              }}
              onDoubleClick={() => taskDblClick && taskDblClick(t)}
              className={
                `row-action-hover-group ${rowBulkPeekClass} !border-b border-b-gray-300/50 flex cursor-pointer items-center ` +
                (selectedTask && selectedTask.id === t.id
                  ? " bg-primary-500/30"
                  : "") +
                (t.isActive === false ? " !bg-[#f3f4f6] opacity-80" : "")
              }
              style={{ height: rowHeight }}
              data-task-id={t.id}
            >
              {columns.map((c, colIndex) => {
                return (
                  <div
                    key={`${t.id ?? "row"}-${colIndex}-${String(c.field)}`}
                    onDoubleClick={() => taskDblClick && taskDblClick(t)}
                    data-bulk-select-cell={
                      c.field === "bulkSelect" ? "" : undefined
                    }
                    className={
                      styles.taskListCell + " last:w-full !overflow-visible"
                    }
                    style={{
                      minWidth: c.width ? `${c.width}px` : rowWidth,
                      maxWidth: c.width ? `${c.width}px` : rowWidth,
                    }}
                  >
                    <div
                      className={
                        styles.taskListNameWrapper +
                        ` ${t.subtask?.length ? "font-semibold " : "" + colIndex
                        } text-[#1F2937]`
                      }
                      style={{
                        marginLeft: ` ${
                          c.field === "name" && t.level.split(".").length > 1
                            ? t.level.split(".").length * 12
                            : 0
                        }px`,
                      }}
                    >
                      {c.field === "name" && (
                        <div
                          className={`flex items-center justify-center ${expanderSymbol
                            ? styles.taskListExpander
                            : styles.taskListEmptyExpander}`
                          }
                          onClick={() => {
                            if (!suppressTaskListExpander && expanderSymbol) {
                              onExpanderClick(t);
                            }
                          }}
                        >
                          {expanderSymbol}
                        </div>
                      )}

                      {c.render ? c.render(t) : `${t[c.field]}`}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };
